import React from 'react';
import utils from 'web3-utils';
import moment from 'moment';

import {
  CAMPAIGN_CREATE_STARTED,
  CAMPAIGN_CREATE_SUBMITTED,
  CAMPAIGN_CREATE_ABORTED,
  CAMPAIGN_CREATE_FAILED,
  CAMPAIGN_CREATE_CONFIRMED,
  CAMPAIGN_CREATE_REJECTED,
  CAMPAIGNS_FETCHED,
  CAMPAIGNS_FETCH_FAILED,
  CAMPAIGN_EVENTS_FETCHED,
  CAMPAIGN_EVENTS_FETCH_FAILED,
  CAMPAIGN_LOCALLY_DISCARDED,
  CAMPAIGN_CANCEL_STARTED,
  CAMPAIGN_CANCEL_SUBMITTED,
  CAMPAIGN_CANCEL_ABORTED,
  CAMPAIGN_CANCEL_FAILED,
  CAMPAIGN_CANCEL_CONFIRMED,
  CAMPAIGN_CANCEL_REJECTED,
  REWARD_CANCEL_STARTED,
  REWARD_CANCEL_SUBMITTED,
  REWARD_CANCEL_ABORTED,
  REWARD_CANCEL_FAILED,
  REWARD_CANCEL_CONFIRMED,
  REWARD_CANCEL_REJECTED,
  CAMPAIGNS_LOCALLY_LOADED,
} from '../actionTypes';
import { rewardsGenerated } from './createCampaign';
import { generateKeyAndCodes } from '../utilities/publicKeyCrypto';
import generateRandomString from '../utilities/generateRandomString';
import { getCampaignDetails } from '../selectors/createCampaign';
import getRewardKey from '../utilities/getRewardKey';
import { CAMPAIGN_ID_LENGTH, REWARD_CODE_LENGTH } from '../constants';

export const createCampaignStart = campaign => ({
  type: CAMPAIGN_CREATE_STARTED,
  campaign,
});

export const createCampaignSubmitted = (
  campaign,
  transactionHash,
  contract,
  date = moment().unix(),
) => ({
  type: CAMPAIGN_CREATE_SUBMITTED,
  campaign: {
    ...campaign,
    timestamp: date,
  },
  contract,
  transactionHash,
});

export const createCampaignAborted = campaign => ({
  type: CAMPAIGN_CREATE_ABORTED,
  campaign,
});

export const createCampaignFailed = (campaign, error) => ({
  type: CAMPAIGN_CREATE_FAILED,
  campaign,
  error,
});

export const createCampaign = (paymentType, viewOptions = {}) => async (
  dispatch,
  getState,
  { ethGateway, tabbyRewards, errorReporter },
) => {
  const {
    name, tokenSymbol, tokenAddress, tokensPerCode, numberOfCodes,
  } = getCampaignDetails(getState());
  const { ethFee, catFee } = getState().app;

  const campaignId = generateRandomString(CAMPAIGN_ID_LENGTH);
  const { address, codes } = generateKeyAndCodes(
    campaignId,
    numberOfCodes,
    generateRandomString(REWARD_CODE_LENGTH),
  );

  dispatch(rewardsGenerated(codes));

  const campaignDetails = {
    id: campaignId,
    name,
    tokenSymbol,
    tokenAddress,
    tokensPerCode,
    numberOfCodes,
    signer: address,
    codes,
    sender: getState().account.address,
  };

  let paymentFee = ethFee;
  let paymentFeeFormatted = `${utils.fromWei(ethFee)} ETH`;
  if (paymentType === 'CAT') {
    paymentFee = catFee;
    paymentFeeFormatted = `${utils.fromWei(catFee)} CAT`;
  }
  const message = (
    <React.Fragment>
      This transaction will create your campaign.<br />
      A payment of <strong>{paymentFeeFormatted}</strong> will be transferred from your wallet.
    </React.Fragment>
  );

  try {
    return await ethGateway.process(getState, {
      create: () =>
        tabbyRewards.createCampaignTransaction(campaignDetails, paymentFee, paymentType === 'CAT'),
      onStart: () => dispatch(createCampaignStart(campaignDetails)),
      onSuccess: transactionHash =>
        dispatch(createCampaignSubmitted(
          campaignDetails,
          transactionHash,
          tabbyRewards.getActiveContract().id,
        )),
      onError: error => dispatch(createCampaignFailed(campaignDetails, error)),
      onAbort: () => dispatch(createCampaignAborted(campaignDetails)),
      viewOptions: {
        ...viewOptions,
        message,
      },
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const createCampaignConfirmed = event => ({
  type: CAMPAIGN_CREATE_CONFIRMED,
  event,
});

export const createCampaignRejected = campaignId => ({
  type: CAMPAIGN_CREATE_REJECTED,
  campaignId,
});

export const campaignsFetched = campaigns => ({
  type: CAMPAIGNS_FETCHED,
  campaigns,
});

export const campaignsFetchFailed = error => ({
  type: CAMPAIGNS_FETCH_FAILED,
  error,
});

export const campaignEventsFetched = events => ({
  type: CAMPAIGN_EVENTS_FETCHED,
  events,
});

export const campaignEventsFetchFailed = error => ({
  type: CAMPAIGN_EVENTS_FETCH_FAILED,
  error,
});

export const discardCampaign = campaignId => ({
  type: CAMPAIGN_LOCALLY_DISCARDED,
  campaignId,
});

export const cancelCampaignStart = campaignId => ({
  type: CAMPAIGN_CANCEL_STARTED,
  campaignId,
});

export const cancelCampaignSubmitted = (campaignId, transactionHash) => ({
  type: CAMPAIGN_CANCEL_SUBMITTED,
  campaignId,
  transactionHash,
});

export const cancelCampaignFailed = (campaignId, error) => ({
  type: CAMPAIGN_CANCEL_FAILED,
  campaignId,
  error,
});

export const cancelCampaignAborted = campaignId => ({
  type: CAMPAIGN_CANCEL_ABORTED,
  campaignId,
});

export const cancelCampaign = (campaign, gasPrice = 3000000, viewOptions = {}) => async (
  dispatch,
  getState,
  { ethGateway, tabbyRewards, errorReporter },
) => {
  try {
    const {
      account: { address },
    } = getState();

    return ethGateway.process(getState, {
      create: () => tabbyRewards.cancelCampaignTransaction(campaign, address, gasPrice),
      onStart: () => dispatch(cancelCampaignStart(campaign.id)),
      onSuccess: cancelTransactionHash =>
        dispatch(cancelCampaignSubmitted(campaign.id, cancelTransactionHash)),
      onError: error => dispatch(cancelCampaignFailed(campaign.id, error)),
      onAbort: () => dispatch(cancelCampaignAborted(campaign.id)),
      viewOptions,
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const cancelCampaignConfirmed = campaignId => ({
  type: CAMPAIGN_CANCEL_CONFIRMED,
  campaignId,
});

export const cancelCampaignRejected = campaignId => ({
  type: CAMPAIGN_CANCEL_REJECTED,
  campaignId,
});

export const cancelRewardStart = rewardKey => ({
  type: REWARD_CANCEL_STARTED,
  rewardKey,
});

export const cancelRewardSubmitted = (rewardKey, transactionHash) => ({
  type: REWARD_CANCEL_SUBMITTED,
  rewardKey,
  transactionHash,
});

export const cancelRewardFailed = (rewardKey, error) => ({
  type: REWARD_CANCEL_FAILED,
  rewardKey,
  error,
});

export const cancelRewardAborted = rewardKey => ({
  type: REWARD_CANCEL_ABORTED,
  rewardKey,
});

export const cancelReward = (
  campaign,
  rewardNumber,
  gasPrice = 3000000,
  viewOptions = {},
) => async (dispatch, getState, { ethGateway, tabbyRewards, errorReporter }) => {
  try {
    const {
      account: { address },
    } = getState();

    const rewardKey = getRewardKey(campaign.id, rewardNumber);

    return ethGateway.process(getState, {
      create: () => tabbyRewards.cancelRewardTransaction(campaign, rewardNumber, address, gasPrice),
      onStart: () => dispatch(cancelRewardStart(rewardKey)),
      onSuccess: cancelTransactionHash =>
        dispatch(cancelRewardSubmitted(rewardKey, cancelTransactionHash)),
      onError: error => dispatch(cancelRewardFailed(rewardKey, error)),
      onAbort: () => dispatch(cancelRewardAborted(rewardKey)),
      viewOptions,
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const cancelRewardConfirmed = rewardKey => ({
  type: REWARD_CANCEL_CONFIRMED,
  rewardKey,
});

export const cancelRewardRejected = rewardKey => ({
  type: REWARD_CANCEL_REJECTED,
  rewardKey,
});

export const campaignsLocallyLoaded = campaigns => ({
  type: CAMPAIGNS_LOCALLY_LOADED,
  campaigns,
});



// WEBPACK FOOTER //
// ./src/actions/campaigns.js